import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useResourceCelebration } from '../contexts/ResourceCelebrationContext';
import FaIcon from './FaIcon';
import { GettonciniIcon } from '../icons';
import styles from '../styles/ResourceCelebrationLayer.module.css';
import { RESOURCE_FLIGHT_DURATION_MS, RESOURCE_RECEIVE_DURATION_MS } from '../utils/resourceCelebrationTiming';

const REDUCED_MOTION_DURATION_MS = 120;
const RESOURCE_FLIGHT_CONFIGS = {
  gettoncini: {
    ariaLabel: 'Gettoncini aggiornati',
    testId: 'gettoncini-resource-flight',
    renderIcon: () => <GettonciniIcon size={20} />,
  },
  stelline: {
    iconClassName: 'fa-solid fa-star',
    ariaLabel: 'Stelline aggiornate',
    testId: 'stelline-resource-flight',
  },
  learned_terms: {
    iconClassName: 'fa-solid fa-learning-books',
    ariaLabel: 'Contatore di parole aggiornato',
    testId: 'learned-terms-resource-flight',
  },
  streak: {
    ariaLabel: 'Serie aggiornata',
    testId: 'streak-resource-flight',
    renderIcon: () => <span className={styles.flameIconGlyph} aria-hidden="true">🔥</span>,
  },
};

const usePrefersReducedMotion = () => {
  const getInitialValue = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getInitialValue);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listener = (event) => setPrefersReducedMotion(event.matches);
    setPrefersReducedMotion(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }

    mediaQuery.addListener(listener);
    return () => mediaQuery.removeListener(listener);
  }, []);

  return prefersReducedMotion;
};

const getStartPoint = () => {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  const railWidthValue = typeof window.getComputedStyle === 'function'
    ? window.getComputedStyle(document.documentElement).getPropertyValue('--page-shell-active-rail-width')
    : '';
  const railWidth = Number.parseFloat(railWidthValue);
  const activeRailWidth = Number.isFinite(railWidth) && railWidth > 0 ? railWidth : 0;

  return {
    x: Math.round((window.innerWidth - activeRailWidth) / 2),
    y: Math.round(window.innerHeight / 2),
  };
};

const getRectCenter = (rect) => ({
  x: Math.round(rect.left + (rect.width / 2)),
  y: Math.round(rect.top + (rect.height / 2)),
});

const ResourceFlight = ({ flight }) => {
  const {
    getTargetRect,
    completeCelebration,
    startCelebration,
    deliverCelebration,
  } = useResourceCelebration();
  const prefersReducedMotion = usePrefersReducedMotion();
  const didImpactRef = useRef(false);
  const [position, setPosition] = useState(() => getStartPoint());
  const [phase, setPhase] = useState('entering');

  const durationMs = prefersReducedMotion ? REDUCED_MOTION_DURATION_MS : RESOURCE_FLIGHT_DURATION_MS;
  const holdMs = prefersReducedMotion ? 180 : flight.holdMs;
  const flightConfig = RESOURCE_FLIGHT_CONFIGS[flight.type] || RESOURCE_FLIGHT_CONFIGS.stelline;

  useEffect(() => {
    const timers = [];
    const targetRect = getTargetRect(flight.type);
    const delayMs = Math.max(0, flight.startAt - Date.now());

    timers.push(setTimeout(() => {
      startCelebration(flight);
      setPhase('shown');
    }, delayMs));

    timers.push(setTimeout(() => {
      const latestTargetRect = getTargetRect(flight.type) || targetRect;
      if (!latestTargetRect) {
        if (!didImpactRef.current) {
          didImpactRef.current = true;
          deliverCelebration(flight.id, { pulseTarget: false });
        }
        completeCelebration(flight.id);
        return;
      }

      setPosition(getRectCenter(latestTargetRect));
      setPhase('flying');
    }, delayMs + holdMs));

    timers.push(setTimeout(() => {
      if (didImpactRef.current) {
        return;
      }
      didImpactRef.current = true;
      deliverCelebration(flight.id);
      setPhase('arrived');
    }, delayMs + holdMs + durationMs));

    timers.push(setTimeout(() => {
      completeCelebration(flight.id);
    }, delayMs + holdMs + durationMs + (prefersReducedMotion ? 220 : RESOURCE_RECEIVE_DURATION_MS)));

    return () => {
      timers.forEach((timerId) => clearTimeout(timerId));
    };
  }, [
    completeCelebration,
    startCelebration,
    durationMs,
    flight,
    getTargetRect,
    holdMs,
    deliverCelebration,
  ]);

  return (
    <div
      className={`${styles.flightPath} ${styles[phase] || ''}`}
      style={{
        '--flight-x': `${position.x}px`,
        '--flight-y': `${position.y}px`,
        '--flight-duration': `${durationMs}ms`,
        '--flight-x-duration': `${Math.round(durationMs * 0.58)}ms`,
      }}
      aria-hidden="true"
      data-resource-flight={flight.type}
      data-testid={flightConfig.testId}
    >
      <div className={`${styles.flightToken} ${styles[phase] || ''} ${styles[flight.type] || ''}`}>
        <span className={styles.tokenValue}>+{flight.delta.toLocaleString('it-IT')}</span>
        <span className={styles.tokenIcon}>
          {typeof flightConfig.renderIcon === 'function'
            ? flightConfig.renderIcon()
            : <FaIcon className={`${flightConfig.iconClassName} ${styles.tokenIconGlyph}`} />}
        </span>
      </div>
    </div>
  );
};

const ResourceCelebrationLayer = () => {
  const { flights } = useResourceCelebration();
  const resourceFlights = useMemo(
    () => flights.filter((flight) => RESOURCE_FLIGHT_CONFIGS[flight?.type]),
    [flights],
  );

  if (!resourceFlights.length) {
    return null;
  }

  return (
    <div className={styles.layer} aria-live="polite" aria-label="Risorse aggiornate">
      {resourceFlights.map((flight) => (
        <ResourceFlight key={flight.id} flight={flight} />
      ))}
    </div>
  );
};

export default ResourceCelebrationLayer;
